import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  MagicLinkRequestSchema,
  SignInRequestSchema,
  type MagicLinkRequest,
  type SignInRequest,
} from '@truecare/schemas';
import { Alert, Button, Card, CardHeader, Field, Input } from '@truecare/ui';
import { ApiError } from '@truecare/api-client';
import { api } from '../api';
import {
  resolveDefaultPostSignInDestination,
  resolveNextRedirect,
} from '../lib/next-redirect';

/**
 * The visible-fallback sign-in surface (system design §8.1 / 04-frontend-
 * principles.md Principle 5). Magic-link and password forms are shown
 * **side-by-side as equals** — never gated behind a "more options"
 * link. A user whose email gateway swallows magic links must see the
 * password option immediately.
 *
 * Two distinct forms, two distinct mutations. Sharing an email field
 * would couple them; keeping them separate lets each form animate
 * its own error / loading state without interference.
 */
export function SignInForms() {
  // Phase 14a-2 — `?next=` lets the admin app bounce unauthenticated
  // operators here then back to the admin destination after sign-in.
  // The search shape is declared on the /sign-in route; both forms read
  // it so a successful sign-in OR magic-link request preserves it
  // through the magic-link verify hop.
  const search = useSearch({ strict: false }) as { next?: string };
  const next = typeof search.next === 'string' ? search.next : null;
  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8">
      <MagicLinkForm next={next} />
      <PasswordForm next={next} />
    </div>
  );
}

interface SignInVariantProps {
  next: string | null;
}

function MagicLinkForm({ next: _next }: SignInVariantProps) {
  const [sent, setSent] = useState(false);
  const form = useForm<MagicLinkRequest>({
    resolver: zodResolver(MagicLinkRequestSchema),
    defaultValues: { email: '' },
  });
  const mutation = useMutation({
    mutationFn: async (input: MagicLinkRequest) => {
      const { error } = await api.POST('/api/v1/auth/magic-link/request', {
        body: input,
      });
      if (error) throw new ApiError('Magic-link request failed', 0, error);
    },
    onSuccess: () => setSent(true),
  });

  return (
    <Card padding="lg">
      <CardHeader
        title="Email me a sign-in link"
        description="We'll send a one-time link to your inbox. Works without remembering a password."
      />
      {sent ? (
        <Alert tone="success" title="Check your email">
          If an account exists for that address, a sign-in link has been sent.
          The link expires in 15 minutes.
        </Alert>
      ) : (
        <form
          onSubmit={form.handleSubmit((data) => mutation.mutate(data))}
          className="flex flex-col gap-4"
          noValidate
        >
          <Field
            label="Email address"
            error={form.formState.errors.email?.message}
          >
            <Input
              type="email"
              placeholder="you@example.com"
              autoComplete="email"
              autoFocus
              {...form.register('email')}
            />
          </Field>
          <Button type="submit" loading={mutation.isPending}>
            Send sign-in link
          </Button>
        </form>
      )}
    </Card>
  );
}

function PasswordForm({ next }: SignInVariantProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const form = useForm<SignInRequest>({
    resolver: zodResolver(SignInRequestSchema),
    defaultValues: { email: '', password: '' },
  });
  const mutation = useMutation({
    mutationFn: async (input: SignInRequest) => {
      const { error: respErr } = await api.POST('/api/v1/auth/sign-in', {
        body: input,
      });
      if (respErr) throw new ApiError('Sign-in failed', 0, respErr);
    },
    onSuccess: async () => {
      // Phase 14b-3 audit C2 — if the signed-in user has TOTP enrolled,
      // their session is NOT yet TOTP-satisfied (sign-in only sets the
      // session cookie; `OperatorGuard` requires `totpSatisfiedAt`).
      // Route them through /sign-in/totp BEFORE following the next-
      // redirect logic so the TOTP page sets the satisfied timestamp
      // and lands them at the intended destination.
      const meResp = await api.GET('/api/v1/auth/me', {});
      // Phase 14 audit fix (2026-06-25) — seed React Query's auth-me
      // cache with the just-fetched user so PortalShell's header
      // re-renders into the signed-in state immediately. Without this,
      // useCurrentUser keeps returning the prior null (sign-in page
      // loaded under a 401-null cache; staleTime = 30s) and the user
      // sees a header with no Sign-out button until they refresh.
      if (meResp.data) {
        queryClient.setQueryData(['auth', 'me'], meResp.data);
      }
      if (meResp.data?.totpEnrolled === true) {
        await navigate({
          to: '/sign-in/totp',
          search: next !== null ? { next } : {},
        });
        return;
      }

      // Phase 14a-2 — validate next URL against the cross-app allowlist
      // BEFORE redirecting; reject anything off-allowlist (open-redirect
      // mitigation).
      const target = resolveNextRedirect(next);
      if (target) {
        // Cross-origin navigation MUST use window.location, not the
        // TanStack Router navigate() — navigate() is same-origin only.
        window.location.href = target;
        return;
      }
      // Phase 14a-2 follow-up — persona-aware default destination.
      // Operator-only users land in the admin app (VITE_ADMIN_URL) so
      // they don't see the /me operator placeholder; customer personas
      // stay on /me.
      const dest = resolveDefaultPostSignInDestination(
        meResp.data?.roles ?? [],
      );
      if (dest.startsWith('http')) {
        window.location.href = dest;
      } else {
        void navigate({ to: dest });
      }
    },
    onError: () => setError('Email or password is incorrect.'),
  });

  return (
    <Card padding="lg">
      <CardHeader
        title="Sign in with password"
        description="Always available — works regardless of email delivery."
      />
      <form
        onSubmit={form.handleSubmit((data) => {
          setError(null);
          mutation.mutate(data);
        })}
        className="flex flex-col gap-4"
        noValidate
      >
        {error && <Alert tone="danger">{error}</Alert>}
        <Field
          label="Email address"
          error={form.formState.errors.email?.message}
        >
          <Input
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            data-testid="signin-email"
            {...form.register('email')}
          />
        </Field>
        <Field
          label="Password"
          error={form.formState.errors.password?.message}
        >
          <Input
            type="password"
            autoComplete="current-password"
            data-testid="signin-password"
            {...form.register('password')}
          />
        </Field>
        <div className="flex items-center justify-between gap-4">
          <Button
            type="submit"
            loading={mutation.isPending}
            data-testid="signin-submit"
          >
            Sign in
          </Button>
          <a
            href="/password-reset/request"
            className="text-sm text-forest hover:text-gold underline-offset-2 hover:underline"
          >
            Forgot password?
          </a>
        </div>
      </form>
    </Card>
  );
}
